import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  AppBar,
  Toolbar,
  IconButton,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Divider,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ContentCopy,
  FormatQuote,
  Settings,
  Code,
  ExpandMore,
  Psychology,
  DataObject,
  Web,
  Style,
  Storage,
  Description,
  Functions,
  Terminal,
} from '@mui/icons-material';

// Enhanced formatting functions
const formatJavaScript = (code, options) => {
  try {
    let formatted = code
      .replace(/([{])\s*/g, '$1 ')
      .replace(/\s*([}])/g, ' $1')
      .replace(/([()])\s*/g, '$1 ')
      .replace(/\s*([()])/g, ' $1')
      .replace(/;\s*/g, ';\n')
      .replace(/\n\s*\n/g, '\n');
    
    return applyIndentation(formatted, options.indent);
  } catch (error) {
    throw new Error('Invalid JavaScript code');
  }
};

const formatHTML = (code, options) => {
  try {
    let formatted = code
      .replace(/>\s*</g, '>\n<')
      .replace(/\s+/g, ' ')
      .trim();
    
    let indentLevel = 0;
    const lines = formatted.split('\n');
    const result = [];
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('</')) {
        indentLevel = Math.max(0, indentLevel - 1);
      }
      
      result.push('  '.repeat(indentLevel) + trimmed);
      
      if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>') && !trimmed.endsWith('-->')) {
        indentLevel++;
      }
    });
    
    return result.join('\n');
  } catch (error) {
    throw new Error('Invalid HTML code');
  }
};

const formatCSS = (code, options) => {
  try {
    let formatted = code
      .replace(/\s*{\s*/g, ' {\n  ')
      .replace(/\s*;\s*/g, ';\n  ')
      .replace(/\s*}\s*/g, '\n}\n')
      .replace(/\s*,\s*/g, ', ')
      .replace(/\n\s*\n/g, '\n');
    
    return formatted.trim();
  } catch (error) {
    throw new Error('Invalid CSS code');
  }
};

const formatJSON = (code, options) => {
  try {
    const parsed = JSON.parse(code);
    return JSON.stringify(parsed, null, options.indent);
  } catch (error) {
    throw new Error('Invalid JSON');
  }
};

const formatPython = (code, options) => {
  try {
    // Basic Python formatting - mainly handles indentation consistency
    const lines = code.split('\n');
    const formattedLines = lines.map(line => {
      // Remove trailing whitespace
      let formattedLine = line.trimEnd();
      
      // Ensure consistent indentation
      const leadingSpaces = line.length - line.trimStart().length;
      if (leadingSpaces > 0) {
        const indentLevel = Math.floor(leadingSpaces / 4) * options.indent;
        formattedLine = ' '.repeat(indentLevel) + formattedLine.trimStart();
      }
      
      return formattedLine;
    });
    
    return formattedLines.join('\n');
  } catch (error) {
    throw new Error('Error formatting Python code');
  }
};

const formatJava = (code, options) => {
  try {
    let formatted = code
      .replace(/([{])\s*/g, '$1\n')
      .replace(/\s*([}])/g, '\n$1')
      .replace(/;\s*/g, ';\n')
      .replace(/\n\s*\n/g, '\n');
    
    return applyIndentation(formatted, options.indent);
  } catch (error) {
    throw new Error('Error formatting Java code');
  }
};

const formatCpp = (code, options) => {
  try {
    let formatted = code
      .replace(/([{])\s*/g, '$1\n')
      .replace(/\s*([}])/g, '\n$1')
      .replace(/;\s*/g, ';\n')
      .replace(/#include\s*(<[^>]+>|"[^"]+")/g, '#include $1')
      .replace(/\n\s*\n/g, '\n');
    
    return applyIndentation(formatted, options.indent);
  } catch (error) {
    throw new Error('Error formatting C++ code');
  }
};

const formatXML = (code, options) => {
  try {
    let formatted = code
      .replace(/>\s*</g, '>\n<')
      .replace(/\s+/g, ' ')
      .trim();
    
    let indentLevel = 0;
    const lines = formatted.split('\n');
    const result = [];
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('</')) {
        indentLevel = Math.max(0, indentLevel - 1);
      }
      
      result.push('  '.repeat(indentLevel) + trimmed);
      
      if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>') && !trimmed.endsWith('-->') && !trimmed.endsWith('?>')) {
        indentLevel++;
      }
    });
    
    return result.join('\n');
  } catch (error) {
    throw new Error('Invalid XML');
  }
};

const formatSQL = (code, options) => {
  try {
    const keywords = ['SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'GROUP BY', 'ORDER BY', 'HAVING', 'CREATE', 'TABLE', 'DATABASE', 'ALTER', 'DROP'];
    
    let formatted = code.toUpperCase();
    
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      formatted = formatted.replace(regex, `\n${keyword}`);
    });
    
    formatted = formatted
      .replace(/,/g, ',\n  ')
      .replace(/\s+/g, ' ')
      .replace(/\n\s*\n/g, '\n')
      .trim();
    
    return applyIndentation(formatted, options.indent);
  } catch (error) {
    throw new Error('Error formatting SQL');
  }
};

const formatMarkdown = (code, options) => {
  try {
    let formatted = code
      .replace(/#\s+/g, '# ')
      .replace(/\*\*\s+/g, '**')
      .replace(/\s+\*\*/g, '**')
      .replace(/\*\s+/g, '* ')
      .replace(/\s+\*/g, '*')
      .replace(/-\s+/g, '- ')
      .replace(/\d+\.\s+/g, match => match.replace(/\s+/, ' '));
    
    // Ensure blank lines between sections
    formatted = formatted
      .replace(/(#+ .*)\n([^#\n])/g, '$1\n\n$2')
      .replace(/(\n)([*-] )/g, '$1$2');
    
    return formatted;
  } catch (error) {
    throw new Error('Error formatting Markdown');
  }
};

// Helper function for indentation
const applyIndentation = (code, indentSize) => {
  const lines = code.split('\n');
  let indentLevel = 0;
  const result = [];
  
  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) {
      result.push('');
      return;
    }
    
    // Decrease indent for closing braces
    if (trimmed.startsWith('}') || trimmed.startsWith(']') || trimmed.startsWith(')')) {
      indentLevel = Math.max(0, indentLevel - 1);
    }
    
    result.push(' '.repeat(indentLevel * indentSize) + trimmed);
    
    // Increase indent for opening braces
    if (trimmed.endsWith('{') || trimmed.endsWith('[') || (trimmed.startsWith('{') && !trimmed.endsWith('}'))) {
      indentLevel++;
    }
  });
  
  return result.join('\n');
};

// Language configurations with icons and examples
const languageConfigs = {
  javascript: {
    name: 'JavaScript',
    icon: <DataObject />,
    formatter: formatJavaScript,
    defaultCode: `function example() {
console.log("Hello World");
const obj = {a:1,b:2};
return obj;
}`,
  },
  python: {
    name: 'Python',
    icon: <Psychology />,
    formatter: formatPython,
    defaultCode: `def calculate_sum(a,b):
total=a+b
print(f"The sum is {total}")
return total

class Calculator:
def __init__(self):
self.result=0`,
  },
  java: {
    name: 'Java',
    icon: <Code />,
    formatter: formatJava,
    defaultCode: `public class Main{public static void main(String[] args){System.out.println("Hello World");int x=5;int y=10;int sum=x+y;}}`,
  },
  cpp: {
    name: 'C++',
    icon: <Terminal />,
    formatter: formatCpp,
    defaultCode: `#include <iostream>
using namespace std;int main(){cout<<"Hello World"<<endl;int arr[5]={1,2,3,4,5};for(int i=0;i<5;i++){cout<<arr[i]<<endl;}return 0;}`,
  },
  html: {
    name: 'HTML',
    icon: <Web />,
    formatter: formatHTML,
    defaultCode: `<div><h1>Title</h1><p>Paragraph text here</p><ul><li>Item 1</li><li>Item 2</li></ul></div>`,
  },
  css: {
    name: 'CSS',
    icon: <Style />,
    formatter: formatCSS,
    defaultCode: `body { margin: 0; padding: 0; } .container { width: 100%; height: 100vh; background: #fff; } .button { color: blue; }`,
  },
  json: {
    name: 'JSON',
    icon: <Storage />,
    formatter: formatJSON,
    defaultCode: `{"name":"John","age":30,"cars":["Ford","BMW","Fiat"],"address":{"street":"123 Main St","city":"Boston"}}`,
  },
  xml: {
    name: 'XML',
    icon: <Description />,
    formatter: formatXML,
    defaultCode: `<root><person><name>John</name><age>30</age><hobbies><hobby>Reading</hobby><hobby>Swimming</hobby></hobbies></person></root>`,
  },
  sql: {
    name: 'SQL',
    icon: <Storage />,
    formatter: formatSQL,
    defaultCode: `SELECT * FROM users WHERE age>18 AND status='active' ORDER BY name ASC, created_at DESC`,
  },
  markdown: {
    name: 'Markdown',
    icon: <Description />,
    formatter: formatMarkdown,
    defaultCode: `# Title\n## Subtitle\nThis is **bold** and *italic* text.\n- Item 1\n- Item 2\n1. First item\n2. Second item`,
  },
};

// Language categories
const languageCategories = {
  web: ['javascript', 'html', 'css', 'json', 'xml'],
  programming: ['python', 'java', 'cpp', 'javascript'],
  data: ['sql', 'json', 'xml'],
  markup: ['markdown', 'html', 'xml'],
};

const CodeFormatterApp = () => {
  const [inputCode, setInputCode] = useState(languageConfigs.javascript.defaultCode);
  const [outputCode, setOutputCode] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [formatOptions, setFormatOptions] = useState({
    indent: 2,
    semicolons: true,
    quotes: 'double',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState('settings');

  const handleFormat = useCallback(() => {
    try {
      const config = languageConfigs[selectedLanguage];
      const formatted = config.formatter(inputCode, formatOptions);
      setOutputCode(formatted);
      setSnackbar({ open: true, message: 'Code formatted successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  }, [inputCode, selectedLanguage, formatOptions]);

  const handleCopy = () => {
    navigator.clipboard.writeText(outputCode);
    setSnackbar({ open: true, message: 'Code copied to clipboard!', severity: 'success' });
  };

  const handleClear = () => {
    setInputCode('');
    setOutputCode('');
  };

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    setInputCode(languageConfigs[newLanguage].defaultCode);
    setOutputCode('');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getLanguageIcon = (lang) => {
    return languageConfigs[lang]?.icon || <Code />;
  };

  return (
    <Box >
      
<br/><br/><br/>
      {/* Title Section */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 4 }}>
        <Container maxWidth="xl">
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Code Formatter
          </Typography>
          {/* Language Chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(languageConfigs).map(([key, config]) => (
              <Chip
                key={key}
                icon={config.icon}
                label={config.name}
                variant={selectedLanguage === key ? "filled" : "outlined"}
                onClick={() => {
                  setSelectedLanguage(key);
                  setInputCode(config.defaultCode);
                }}
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  bgcolor: selectedLanguage === key ? 'primary.light' : 'transparent',
                  mb: 1 
                }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Configuration Panel */}
          <Grid item xs={12} md={3}>
            <Accordion 
              expanded={expandedAccordion === 'settings'} 
              onChange={handleAccordionChange('settings')}
              elevation={3}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Settings sx={{ mr: 1 }} />
                  Formatting Settings
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={selectedLanguage}
                    label="Language"
                    onChange={handleLanguageChange}
                  >
                    {Object.entries(languageConfigs).map(([key, config]) => (
                      <MenuItem key={key} value={key} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1, display: 'flex' }}>{config.icon}{config.name}</Box>
                        
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Indentation</InputLabel>
                  <Select
                    value={formatOptions.indent}
                    label="Indentation"
                    onChange={(e) => setFormatOptions({ ...formatOptions, indent: e.target.value })}
                  >
                    <MenuItem value={2}>2 spaces</MenuItem>
                    <MenuItem value={4}>4 spaces</MenuItem>
                    <MenuItem value={8}>8 spaces</MenuItem>
                    <MenuItem value={'\t'}>Tabs</MenuItem>
                  </Select>
                </FormControl>

                {(selectedLanguage === 'javascript' || selectedLanguage === 'python') && (
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Quotes</InputLabel>
                    <Select
                      value={formatOptions.quotes}
                      label="Quotes"
                      onChange={(e) => setFormatOptions({ ...formatOptions, quotes: e.target.value })}
                    >
                      <MenuItem value="double">Double quotes</MenuItem>
                      <MenuItem value="single">Single quotes</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {(selectedLanguage === 'javascript' || selectedLanguage === 'java' || selectedLanguage === 'cpp') && (
                  <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formatOptions.semicolons}
                          onChange={(e) => setFormatOptions({ ...formatOptions, semicolons: e.target.checked })}
                        />
                      }
                      label="Include semicolons"
                    />
                  </Box>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleFormat}
                  startIcon={<Code />}
                  sx={{ mb: 2 }}
                >
                  Format Code
                </Button>
                
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleClear}
                    >
                      Clear All
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleCopy}
                      startIcon={<ContentCopy />}
                      disabled={!outputCode}
                    >
                      Copy
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Language Categories */}
            <Accordion 
              expanded={expandedAccordion === 'categories'} 
              onChange={handleAccordionChange('categories')}
              sx={{ mt: 2 }}
              elevation={3}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Language Categories</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Web Development
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {languageCategories.web.map(lang => (
                        <Chip
                          key={lang}
                          icon={getLanguageIcon(lang)}
                          label={languageConfigs[lang].name}
                          size="small"
                          variant={selectedLanguage === lang ? "filled" : "outlined"}
                          onClick={() => {
                            setSelectedLanguage(lang);
                            setInputCode(languageConfigs[lang].defaultCode);
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Programming Languages
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {languageCategories.programming.map(lang => (
                        <Chip
                          key={lang}
                          icon={getLanguageIcon(lang)}
                          label={languageConfigs[lang].name}
                          size="small"
                          variant={selectedLanguage === lang ? "filled" : "outlined"}
                          onClick={() => {
                            setSelectedLanguage(lang);
                            setInputCode(languageConfigs[lang].defaultCode);
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Data Formats
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {languageCategories.data.map(lang => (
                        <Chip
                          key={lang}
                          icon={getLanguageIcon(lang)}
                          label={languageConfigs[lang].name}
                          size="small"
                          variant={selectedLanguage === lang ? "filled" : "outlined"}
                          onClick={() => {
                            setSelectedLanguage(lang);
                            setInputCode(languageConfigs[lang].defaultCode);
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Code Editor Panel */}
          <Grid item xs={12} md={9}>
            <Card elevation={3}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  '& .MuiTab-root': { fontWeight: 'bold' }
                }}
              >
                <Tab 
                  label="Input Code" 
                  icon={<Description />} 
                  iconPosition="start"
                />
                <Tab 
                  label="Formatted Output" 
                  icon={<FormatQuote />} 
                  iconPosition="start"
                />
              </Tabs>
              
              <Divider />
              
              <CardContent sx={{ p: 0 }}>
                {activeTab === 0 && (
                  <TextField
                    fullWidth
                    multiline
                    rows={22}
                    value={inputCode}
                    onChange={(e) => setInputCode(e.target.value)}
                    variant="outlined"
                    placeholder={`Enter your ${languageConfigs[selectedLanguage].name} code here...`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        lineHeight: '1.5',
                      },
                    }}
                  />
                )}
                
                {activeTab === 1 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      minHeight: '500px',
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      whiteSpace: 'pre-wrap',
                      color: outputCode ? 'text.primary' : 'text.disabled',
                      lineHeight: '1.5',
                    }}
                  >
                    {outputCode || 'Formatted code will appear here after clicking "Format Code"...'}
                  </Paper>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setInputCode(languageConfigs[selectedLanguage].defaultCode)}
              >
                Load Example
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigator.clipboard.readText().then(text => setInputCode(text))}
              >
                Paste from Clipboard
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setInputCode('')}
              >
                Clear Input
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CodeFormatterApp;